/**
 * 문제집 카드 컴포넌트
 * 배정된 문제집 정보를 레트로 게임 스타일로 표시
 */

import React from 'react';
import { AssignedWorkbook } from '../../types/workbook';
import { PixelCard } from '../common/PixelCard';
import { PixelButton } from '../common/PixelButton';
import { ProgressBadge } from './ProgressBadge';

/**
 * WorkbookCard 컴포넌트 Props
 */
interface WorkbookCardProps {
  /** 배정된 문제집 정보 */
  assignment: AssignedWorkbook;
  /** 문제집 선택 핸들러 */
  onSelect: (workbookName: string) => void;
}

/**
 * 문제집 카드 컴포넌트
 *
 * - 문제집 제목 및 정보 (년도, 학년, 학기)
 * - 진도 배지 (완료/진행중/마감)
 * - 마감일 표시
 * - 문제 풀기 버튼
 */
export const WorkbookCard: React.FC<WorkbookCardProps> = ({
  assignment,
  onSelect,
}) => {
  const { workbooks, is_completed, due_date, workbook_name } = assignment;

  // workbooks 조인 데이터가 없으면 폴백으로 workbook_name 사용
  const displayName = workbooks?.name || workbook_name;
  const displayYear = workbooks?.year || new Date().getFullYear();
  const displayGrade = workbooks?.grade || '학년 정보 없음';
  const displaySemester = workbooks?.semester || null;

  /**
   * 마감일 포맷팅
   * @param dateStr - ISO 8601 날짜 문자열
   * @returns 한국어 날짜 문자열
   */
  const formatDueDate = (dateStr: string | null): string | null => {
    if (!dateStr) return null;

    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return null;
    }
  };

  return (
    <PixelCard
      variant="panel"
      padding="medium"
      className="hover:scale-[1.02] transition-transform duration-200"
    >
      {/* 상단: 제목 및 배지 */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="font-pixel text-lg text-[#9bbc0f] mb-1 break-words">
            📚 {displayName}
          </h3>
          <p className="font-pixel text-xs text-gray-400">
            {displayYear}년 | {displayGrade}
            {displaySemester && ` | ${displaySemester}`}
          </p>
        </div>
        <div className="ml-2 flex-shrink-0">
          <ProgressBadge isCompleted={is_completed} dueDate={due_date} />
        </div>
      </div>

      {/* 마감일 표시 (진행중인 경우만) */}
      {due_date && !is_completed && (
        <p className="font-pixel text-xs text-gray-500 mb-3">
          📅 마감: {formatDueDate(due_date)}
        </p>
      )}

      {/* 완료일 표시 (완료된 경우) */}
      {is_completed && assignment.completed_at && (
        <p className="font-pixel text-xs text-gray-500 mb-3">
          ✅ 완료: {formatDueDate(assignment.completed_at)}
        </p>
      )}

      {/* 액션 버튼 */}
      <PixelButton
        variant={is_completed ? 'secondary' : 'primary'}
        size="medium"
        onClick={() => onSelect(displayName)}
        className="w-full"
        disabled={is_completed}
      >
        {is_completed ? '✓ 완료됨' : '🎮 문제 풀기 START'}
      </PixelButton>
    </PixelCard>
  );
};
